import { useEffect, useState, useCallback } from "react";
import { Loader2, Lock, CheckCircle2, AlertCircle, Users, Zap, RefreshCw, Wallet } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth-context";
import { startSubscriptionCheckout, startPayPalCheckout, pollSubscriptionActive, currencySymbol } from "../../lib/billing";
import { toast } from "sonner";

const STROKE = 1.5;

const STATUS_BADGE = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-800" },
  past_due: { label: "Past due", cls: "bg-amber-100 text-amber-800" },
  cancelled: { label: "Cancelled", cls: "bg-ink-200 text-ink-700" },
  authenticated: { label: "Pending", cls: "bg-amber-100 text-amber-800" },
  created: { label: "Pending", cls: "bg-amber-100 text-amber-800" },
};

export default function BillingPanel({ isAdmin }) {
  const { user, refresh } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "monthly" | "annual" | "verifying" | "cancel" | "sync"

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/info");
      setBilling(data);
    } catch {
      setBilling(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // PayPal approval round-trip: PayPal redirects back to
  // /settings?paypal=return|cancelled. Activation is webhook-driven — we only
  // poll /billing/info here, never trust the redirect itself.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pp = params.get("paypal");
    if (!pp) return;
    window.history.replaceState({}, "", window.location.pathname);
    if (pp === "cancelled") {
      toast.message("PayPal checkout was cancelled.");
      return;
    }
    if (pp === "return") {
      setBusy("verifying");
      toast.message("Approval received — confirming with PayPal…");
      pollSubscriptionActive().then(async (active) => {
        if (active) {
          toast.success("Subscription active. You're on the Pro plan.");
          refresh?.();
        } else {
          toast.message("Approval received. Activation will appear once PayPal confirms via webhook.");
        }
        await load();
        setBusy(null);
      });
    }
  }, [load, refresh]);

  const isPaid = billing?.plan === "pro" || billing?.subscription_status === "active";
  const configured = !!billing?.razorpay_configured;
  const paypalConfigured = !!billing?.paypal_configured;
  const anyConfigured = configured || paypalConfigured;
  const sym = currencySymbol(billing?.currency || "USD");

  const handleUpgrade = async (period) => {
    setBusy(period);
    try {
      const res = await startSubscriptionCheckout({ period, user });
      if (res.status === "submitted") {
        setBusy("verifying");
        toast.message("Payment submitted — confirming with Razorpay…");
        const active = await pollSubscriptionActive();
        if (active) {
          toast.success("Subscription active. You're on the Pro plan.");
          await load();
          refresh?.();
        } else {
          toast.message("Payment received. Activation will appear once Razorpay confirms via webhook.");
          await load();
        }
      }
    } catch (e) {
      const code = e?.response?.data?.detail;
      toast.error(code === "billing_not_configured" ? "Billing isn't configured yet." : "Could not start checkout.");
    } finally {
      setBusy(null);
    }
  };

  const handlePayPal = async (period) => {
    setBusy(`paypal-${period}`);
    try {
      await startPayPalCheckout({ period });
      // Full-page redirect to PayPal follows; nothing more to do here.
    } catch (e) {
      const code = e?.response?.data?.detail;
      toast.error(code === "billing_not_configured" ? "PayPal isn't configured yet." : "Could not start PayPal checkout.");
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/subscription/cancel");
      toast.success("Cancellation requested. Access continues until the end of the billing period.");
      await load();
    } catch {
      toast.error("Could not request cancellation.");
    } finally {
      setBusy(null);
    }
  };

  const handleSyncSeats = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/billing/subscription/sync-seats");
      toast.success(`Seats synced to ${data.quantity}. Applies next billing cycle.`);
      await load();
    } catch {
      toast.error("Could not sync seats.");
    } finally {
      setBusy(null);
    }
  };

  const badge = STATUS_BADGE[billing?.subscription_status] || null;

  return (
    <section className="hp-card" data-testid="billing-section">
      <div className="border-b border-ink-200 px-6 py-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-medium text-ink-900">Billing</h2>
          <p className="mt-1 text-[13px] text-ink-500">
            Seat-based plan. Viewers (read-only audit access) are always free.
          </p>
        </div>
        {isPaid ? (
          <Zap className="h-4 w-4 text-emerald-700" strokeWidth={STROKE} />
        ) : (
          <Lock className="h-4 w-4 text-ink-400" strokeWidth={STROKE} />
        )}
      </div>

      <div className="p-6" data-testid="billing-panel">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div>
            <div className="hp-section-label">Plan</div>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-[15px] font-medium capitalize text-ink-900" data-testid="billing-plan">
                {loading ? "—" : isPaid ? "Pro" : "Free"}
              </span>
              {badge && (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`} data-testid="billing-status-badge">
                  {badge.label}
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="hp-section-label">Billable seats</div>
            <div className="mt-1.5 text-[15px] font-medium text-ink-900" data-testid="billing-seats">
              {loading ? "—" : billing?.billable_seats ?? "—"}
            </div>
          </div>
          <div>
            <div className="hp-section-label">Free interviews used</div>
            <div className="mt-1.5 text-[15px] font-medium text-ink-900" data-testid="billing-free-used">
              {loading ? "—" : `${billing?.free_interviews_used ?? 0} / ${billing?.free_interviews_total ?? 2}`}
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-ink-200 pt-5">
          {!isAdmin ? (
            <p className="text-[13px] text-ink-500">Ask a workspace admin to manage billing.</p>
          ) : !anyConfigured ? (
            <div className="flex items-start gap-2 text-[13px] text-ink-500">
              <AlertCircle className="mt-0.5 h-4 w-4 text-ink-400" strokeWidth={STROKE} />
              <span>Billing isn't configured in this environment yet. You can use Hiretape on the free plan.</span>
            </div>
          ) : isPaid ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[13px] text-emerald-800">
                <CheckCircle2 className="h-4 w-4" strokeWidth={STROKE} />
                You're on the Pro plan — unlimited interviews.
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={handleSyncSeats} disabled={busy} className="hp-btn-outline" data-testid="billing-sync-seats-btn">
                  {busy === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" strokeWidth={STROKE} />}
                  Sync seats
                </button>
                <button onClick={handleCancel} disabled={busy} className="hp-btn-outline text-rose-700" data-testid="billing-cancel-btn">
                  {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Cancel subscription
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[13px] text-ink-600">
                Upgrade to keep recording interviews after your {billing?.free_interviews_total ?? 2} free ones.
                Billed per active member (recruiters, hiring managers, admins).
              </p>
              <div className="flex flex-wrap gap-3">
                {configured && (
                  <>
                    <button onClick={() => handleUpgrade("monthly")} disabled={!!busy} className="hp-btn-primary" data-testid="billing-upgrade-monthly-btn">
                      {busy === "monthly" || busy === "verifying" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" strokeWidth={STROKE} />}
                      Upgrade — {sym}{billing?.price_per_seat_monthly}/seat/mo
                    </button>
                    <button onClick={() => handleUpgrade("annual")} disabled={!!busy} className="hp-btn-outline" data-testid="billing-upgrade-annual-btn">
                      {busy === "annual" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" strokeWidth={STROKE} />}
                      Annual — {sym}{billing?.price_per_seat_annual}/seat/yr (2 months free)
                    </button>
                  </>
                )}
                {paypalConfigured && (
                  <>
                    <button onClick={() => handlePayPal("monthly")} disabled={!!busy} className={configured ? "hp-btn-outline" : "hp-btn-primary"} data-testid="billing-paypal-monthly-btn">
                      {busy === "paypal-monthly" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" strokeWidth={STROKE} />}
                      PayPal — {sym}{billing?.price_per_seat_monthly}/seat/mo
                    </button>
                    <button onClick={() => handlePayPal("annual")} disabled={!!busy} className="hp-btn-outline" data-testid="billing-paypal-annual-btn">
                      {busy === "paypal-annual" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" strokeWidth={STROKE} />}
                      PayPal annual — {sym}{billing?.price_per_seat_annual}/seat/yr
                    </button>
                  </>
                )}
              </div>
              {busy === "verifying" && (
                <p className="text-[12px] text-ink-400" data-testid="billing-verifying">Confirming your payment…</p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
